import { useEffect, useState } from "react";
import { canCreate } from '../../util/access';
import { DialogContent, FormControlLabel, Grid, Switch, TextField } from "@mui/material";
import { PersonAddAlt1 } from "@mui/icons-material";
import { toast } from "react-toastify";

import {
	MoleCancelButton,
	MoleDialog,
	MoleDialogActions,
	MoleDialogHeader,
	MolePrimaryButton,
	useMoleThemeColor,
} from "../../components/MoleDialogShell";
import { useAppSocket } from "../../providers/AppSocketContext";
import { useAuth } from "../../hooks/useAuth";
import { useUI } from "../../providers/MoleUIContext";
import { MONEY_LIMITS, parseMoneyInput } from "../../util/quantity";
import { MYSQL_LIMITS } from "../../util/constants";
import { ContactBlock } from "../../util/Batch";
import { Util } from "../../util/Util";
import { contactToPayload } from "./contactBlock";

const ACCOUNT_NAME_MAX = 50;

export interface CreatedClient {
	id: number;
	acc_name: string;
	is_agent?: boolean;
	credit_limit?: number;
}

interface Props {
	open: boolean;
	onClose: () => void;
	/** Contact block typed on the document; seeds the name and is saved to the new client. */
	prefill?: ContactBlock | null;
	/** Clients already loaded, used to warn about a duplicate name before posting. */
	existingNames?: readonly string[];
	onClientCreated: (client: CreatedClient) => void;
}

const ClientCreateDialog = ({ open, onClose, prefill, existingNames = [], onClientCreated }: Props) => {
	const { api, access } = useAuth();
	const { t, queueReRun } = useUI();
	const themeColor = useMoleThemeColor();

	const { appSettings } = useAppSocket();
	// Agents only make sense when the credit policy has a place for them.
	const creditPolicy = String(appSettings?.CREDIT_POLICY?.value || "NONE").toUpperCase();
	const allowsAgents = creditPolicy === "AGENT" || creditPolicy === "BOTH";

	const [accName, setAccName] = useState("");
	const [creditLimit, setCreditLimit] = useState("");
	const [isAgent, setIsAgent] = useState(false);
	// Columns of the client resource: the backend refuses a payload naming one
	// they may not set, so the field goes rather than failing on save.
	const canSetCreditLimit = canCreate(access, "client", "credit_limit");
	const canSetAgent = canCreate(access, "client", "is_agent");
	const [saving, setSaving] = useState(false);
	const [nameError, setNameError] = useState("");

	useEffect(() => {
		if (!open) return;
		setAccName((prefill?.con_name || "").slice(0, ACCOUNT_NAME_MAX));
		setCreditLimit("");
		setIsAgent(false);
		setNameError("");
	}, [open, prefill]);

	const trimmed = accName.trim();
	const duplicate = existingNames.some((name) => Util.compareStrings(name || "", trimmed));
	const canSave = trimmed.length > 0 && !duplicate && !saving;

	const handleSave = async () => {
		if (!canSave) return;
		setSaving(true);
		setNameError("");

		const res = await api.post("/clients", {
			acc_name: trimmed,
			// New clients from the sales screens are always consumers.
			acc_type: "CONSUMER",
			...(canSetAgent ? { is_agent: allowsAgents && isAgent } : {}),
			...(canSetCreditLimit && creditLimit.trim()
				? { credit_limit: parseMoneyInput(creditLimit) ?? 0 }
				: {}),
			...(contactToPayload(prefill) ? { contact: contactToPayload(prefill) } : {}),
		});

		setSaving(false);

		if (!res) return;
		if (res.status === 201) {
			onClientCreated(res.data);
			toast.success(t("common:toast.success.client"), { autoClose: 3000 });
			onClose();
			return;
		}
		if (res.status === 409) {
			setNameError(res.data?.message || t("common:client.create.nameExists"));
			return;
		}
		if (res.status === 401 || res.status === 422) {
			queueReRun(handleSave);
			return;
		}
		toast.error(res.data?.message || t("common:toast.error.integrity"), { autoClose: 3000 });
	};

	return (
		<MoleDialog open={open} onClose={onClose} maxWidth="xs" fullWidth themeColor={themeColor}>
			<MoleDialogHeader
				title={t("common:client.create.title")}
				icon={<PersonAddAlt1 />}
				onClose={onClose}
				themeColor={themeColor}
			/>
			<DialogContent sx={{ pt: 2 }}>
				<Grid container spacing={2}>
					<Grid size={{ xs: 12 }}>
						<TextField
							autoFocus
							fullWidth
							size="small"
							variant="filled"
							label={t("common:client.create.accName")}
							value={accName}
							inputProps={{ maxLength: ACCOUNT_NAME_MAX }}
							onChange={(e) => {
								setAccName(e.target.value);
								setNameError("");
							}}
							error={Boolean(nameError) || duplicate}
							helperText={
								nameError
								|| (duplicate ? t("common:client.create.nameExists") : "")
								|| t("common:client.create.nameHint", { max: ACCOUNT_NAME_MAX })
							}
						/>
					</Grid>
					<Grid size={{ xs: 12, sm: 6 }}>
						<TextField
							disabled
							fullWidth
							size="small"
							variant="filled"
							label={t("common:client.create.accType")}
							value={t("common:client.create.typeConsumer")}
						/>
					</Grid>
					{canSetCreditLimit && (
						<Grid size={{ xs: 12, sm: 6 }}>
							<TextField
								fullWidth
								size="small"
								type="number"
								variant="filled"
								label={t("common:client.create.creditLimit")}
								value={creditLimit}
								inputProps={{ min: 0, max: MONEY_LIMITS.max, step: "0.01" }}
								onChange={(e) => setCreditLimit(e.target.value)}
								helperText={t("common:client.create.creditLimitHint")}
							/>
						</Grid>
					)}
					{allowsAgents && canSetAgent && (
						<Grid size={{ xs: 12 }} display="flex" alignItems="center">
							<FormControlLabel
								control={<Switch checked={isAgent} onChange={(e) => setIsAgent(e.target.checked)} />}
								label={t("common:client.create.isAgent")}
							/>
						</Grid>
					)}
					{contactToPayload(prefill) && (
						<Grid size={{ xs: 12 }}>
							<TextField
								fullWidth
								multiline
								size="small"
								variant="filled"
								label={t("common:client.create.contactSaved")}
								value={[prefill?.con_phone, prefill?.con_email, prefill?.con_address]
									.map((value) => String(value ?? "").trim())
									.filter(Boolean)
									.join("\n")}
								InputProps={{ readOnly: true }}
								inputProps={{ maxLength: MYSQL_LIMITS.TEXT.maxLength }}
							/>
						</Grid>
					)}
				</Grid>
			</DialogContent>
			<MoleDialogActions>
				<MoleCancelButton onClick={onClose}>{t("common:ui.buttons.cancel")}</MoleCancelButton>
				<MolePrimaryButton onClick={handleSave} disabled={!canSave}>
					{t("common:ui.buttons.save")}
				</MolePrimaryButton>
			</MoleDialogActions>
		</MoleDialog>
	);
};

export default ClientCreateDialog;
